import { Checkbox, useTheme } from '@fluentui/react'
import { RootState } from 'app/store'
import LookupControl from 'components/controls/LookupControl'
import { EHideReason } from 'global/Visibility/EHideReason'
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type'
import * as IXrmTypes from 'model/CRM'
import * as FAM from 'model/FlexibleAttributeModel'
import React, { useState } from 'react'
import AttributeSetGridView from 'views/BlueRelationship/AttributeSetGridView'
import ComparisonAttributeSetGridView from 'views/Comparison/ComparisonAttributeSetGridView'
import ControlDebug from './controls/ControlDebug'
import Costshare from './controls/Costshare'
import Currency from './controls/Currency'
import DatePickerControl from './controls/DatePickerControl'
import InformationCallout from './controls/InformationCallout'
import MultiTextField from './controls/MultiTextField'
import MultiLine from './controls/Multiline'
import OptionControl from './controls/OptionControl'
import OptionsetControl from './controls/OptionsetControl'
import PlaceHolderControl from './controls/PlaceHolderControl'
import SliderPercentControl from './controls/SliderPercentControl'
import TextFieldControl from './controls/TextFieldControl'
import WholeNumber, { EFormat } from './controls/WholeNumber'
import MultiselectOptionsetControl from './controls/MultiselectOptionsetControl'

export enum EControlType {
    boolean = 808900000,
    currency = 808900002,
    date = 808900003,
    decimal = 808900004,
    percent = 808900005,
    text = 808900008,
    optionset = 808900007,
    multiText = 808900009,
    wholeNumber = 808900010,
    message = 808900014,

    costshare = 808900001,
    visionCostshare = 808900012,
    pharmacyCostshare = 808900013,
    attributeSet = 808900015,
    lookup = 808900006,
    multiOptionset = 808900016,


    // retire the rest
    checkBox = 1,
    range = 6
}


interface IControlProps {
    [key: string]: any;
    label?: string;
    controlType: EControlType;
    disabled?: boolean;
    field: FAM.IFlexibleField;
    fieldValueOnChange?: (event: React.FormEvent<HTMLElement | HTMLInputElement>, value: FAM.TValue | FAM.IStringValue | FAM.INumberValue | FAM.IDateValue) => void
    defaultFieldValue?: any;
    config: FAM.IGroupConfigurationConfig;
    locked?: boolean;
    category?: IXrmTypes.bcbs_config_category;
    costshareCategory?: IXrmTypes.bcbs_config_category;
    scope?: FAM.TScope;
    masterAttribute?: IXrmTypes.bcbs_config_masterattribute;
    configAttribute?: IXrmTypes.bcbs_config_attribute;
    attribute?: IXrmTypes.bcbs_config_attribute;
    state: RootState;
    isHiddenReason?: EHideReason;
    comparison?: boolean | null;
    side?: 'left' | 'right' | 'merge' | undefined;
    visibility?: IVisibilityScope
}

interface ITextControlProps extends IControlProps {
    controlType: EControlType.text
    handleChange?: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
}

interface IMultiTextControlProps extends IControlProps {
    controlType: EControlType.multiText
}

interface ICheckboxControlProps extends IControlProps {
    controlType: EControlType.checkBox
}

interface IOptionsetControlProps extends IControlProps {
    controlType: EControlType.optionset
}

interface IBooleanControlProps extends IControlProps {
    controlType: EControlType.boolean;
}

interface IDateControlProps extends IControlProps {
    controlType: EControlType.date;
}

interface IRangeProps extends IControlProps {
    controlType: EControlType.range;
    min: number;
    max: number;
}

interface IPercentControlProps extends IControlProps {
    controlType: EControlType.percent;
}

interface IMessageControlProps extends IControlProps {
    controlType: EControlType.message;
}
interface ICurrencyControlProps extends IControlProps {
    controlType: EControlType.currency;
}

interface IWholeNumberControlProps extends IControlProps {
    controlType: EControlType.wholeNumber;
}

interface IDecimalControlProps extends IControlProps {
    controlType: EControlType.decimal;
}

interface ICostshareControlProps extends IControlProps {
    controlType: EControlType.costshare;
}

interface IVisionCostshareControlProps extends IControlProps {
    controlType: EControlType.visionCostshare;
}

interface IPharmacyCostshareControlProps extends IControlProps {
    controlType: EControlType.pharmacyCostshare;
}

interface IAttributeSetControlProps extends IControlProps {
    controlType: EControlType.attributeSet;
}

interface ILookupControlProps extends IControlProps {
    controlType: EControlType.lookup;
}

interface IMultiOptionsetSetControlProps extends IControlProps {
    controlType: EControlType.multiOptionset;
}

type TControlWrapperTypes =
    ITextControlProps |
    IBooleanControlProps |
    ICheckboxControlProps |
    IOptionsetControlProps |
    IDateControlProps |
    IRangeProps |
    IPercentControlProps |
    IMessageControlProps |
    ICurrencyControlProps |
    IWholeNumberControlProps |
    IDecimalControlProps |
    IMultiTextControlProps |
    ICostshareControlProps |
    IVisionCostshareControlProps |
    IPharmacyCostshareControlProps |
    ILookupControlProps |
    IAttributeSetControlProps |
    IMultiOptionsetSetControlProps;


const styleHidden: React.CSSProperties = { opacity: '0.3' };

const ControlWrapper: React.FunctionComponent<TControlWrapperTypes> = (props) => {
    const theme = useTheme();
    const [locked, setLocked] = useState(props.locked ?? false);

    let content = <></>;

    if (locked)
        return <PlaceHolderControl label={props.label} onClick={() => setLocked(false)} />

    switch (props.controlType) {

        case EControlType.optionset:
            content = <OptionsetControl {...props} />;
            break;

        case EControlType.boolean:
            content = <OptionControl {...props} />;
            break;

        case EControlType.percent:
            content = <SliderPercentControl {...props} />;
            break;

        case EControlType.date:
            content = <DatePickerControl {...props} />;
            break;

        case EControlType.text:
            content = <TextFieldControl {...props} />;
            break;

        case EControlType.checkBox:
            content = <Checkbox {...props} />;
            break;

        case EControlType.multiText:
            content = <MultiTextField {...props} />;
            break;

        case EControlType.decimal:
            content = <WholeNumber format={EFormat.float2} {...props} />;
            break;

        case EControlType.wholeNumber:
            content = <WholeNumber format={EFormat.int} {...props} />;
            break;

        case EControlType.currency:
            content = <Currency {...props} />;
            break;

        case EControlType.message:
            content = <MultiLine {...props} />;
            break;

        case EControlType.lookup:
            content = <LookupControl {...props} />;
            break;

        case props.comparison && EControlType.attributeSet:
            //@ts-ignore
            content = <ComparisonAttributeSetGridView  {...props} currentBenefit={props.scope} leftright={props.side} />;
            break;

        case !props.comparison && EControlType.attributeSet:
            //@ts-ignore
            content = <AttributeSetGridView  {...props} />;
            break;

        case EControlType.costshare:
            content = <Costshare {...props} />;
            break;

        case EControlType.visionCostshare:
            content = <Costshare {...props} />;
            break;

        case EControlType.pharmacyCostshare:
            content = <Costshare {...props} />;
            break;

        case EControlType.multiOptionset:
            content = <MultiselectOptionsetControl {...props} />;
            break;

        default:
            console.error('Error:No controlType detected/Falling Back to default', props.controlType)
            content = (<>Error: No controlType detected {props.controlType}</>)
    }

    let style: React.CSSProperties | undefined = undefined;

    if (!props.comparison && props.field?.visibility)
        style = styleHidden;

    if (props.state.status.highlightFieldId === props.configAttribute?.bcbs_config_attributeid)
        style = {
            ...style,
            border: 'thin solid #fdecb2',
            backgroundColor: theme.name === 'default' ? '#fdecb2' : '#655930',
            padding: '6px',
            borderRadius: '6px'
        };

    return (
        <div id={props?.id} style={style}>
            {props?.state?.status?.debug.diagnosis &&
                <ControlDebug {...props} />
            }

            {props?.configAttribute?.bcbs_helptext &&
                <FieldInformationCallout
                    configAttribute={props.configAttribute}
                />
            }
            {content}
        </div>
    );
}

interface IFieldInformationCallout {
    configAttribute: IXrmTypes.bcbs_config_attribute;
}
const FieldInformationCallout: React.FC<IFieldInformationCallout> = (props) => {
    return <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: '-24px', top: '26px' }}>
            <InformationCallout>
                <div>{props.configAttribute?.bcbs_helptext}</div>
            </InformationCallout>
        </div>
    </div>
}

export default ControlWrapper


